import { z } from 'zod';
import { CODIGOS_DE_OBSERVACION, UNIDADES, } from '../catalogos/observaciones.js';
import { esquemaId } from './comunes.js';
import { esquemaInstante } from './cita.js';
import { problemasDeObservacion, } from './observacion.js';
/**
 * Oftalmologia (F6.1, SPEC-01, plan maestro §7.4).
 *
 * Lo que se mide por ojo nunca se resume en un solo numero: la comparacion
 * devuelve una serie por ojo y por notacion, y un informe importado dice el ojo
 * de cada valor con su etiqueta, que se traduce con una tabla explicita.
 */
/** Mediciones del paquete, en el orden en que se muestran. */
export const CODIGOS_OFTALMOLOGICOS = [
    'agudeza-visual-snellen',
    'agudeza-visual-decimal',
    'agudeza-visual-logmar',
    'refraccion',
    'presion-intraocular',
];
/**
 * Etiquetas de ojo que se aceptan en un informe importado. OD y OI son las de
 * la practica en espanol; OS es la latina del izquierdo; AO y OU, ambos ojos.
 * Una etiqueta que no este aqui (D, I, R, L…) se rechaza: adivinar el ojo es
 * justo el error que esto evita.
 */
export const LADO_DE_ETIQUETA_DE_OJO = {
    OD: 'derecha',
    OI: 'izquierda',
    OS: 'izquierda',
    AO: 'bilateral',
    OU: 'bilateral',
};
export function ladoDeEtiquetaDeOjo(etiqueta) {
    const clave = etiqueta.trim().toUpperCase();
    return Object.hasOwn(LADO_DE_ETIQUETA_DE_OJO, clave)
        ? (LADO_DE_ETIQUETA_DE_OJO[clave] ?? null)
        : null;
}
const esquemaMedicionImportada = z
    .object({
    ojo: z.string().trim().min(1).max(4),
    codigo: z.enum(CODIGOS_DE_OBSERVACION),
    valor: z.number().finite().optional(),
    componentes: z.record(z.string().trim().min(1).max(40), z.number().finite()).optional(),
    unidad: z.enum(UNIDADES),
    metodo: z.string().trim().max(120).default(''),
})
    .strict();
/**
 * Informe de un equipo (autorrefractometro, tonometro…) ya estructurado. Entra
 * como `dispositivo` o `importado`: queda pendiente de revision y no se
 * convierte en dato profesional por si solo.
 */
export const esquemaImportarInformeOftalmologico = z
    .object({
    pacienteId: esquemaId,
    encuentroId: esquemaId.optional(),
    medidaEn: esquemaInstante,
    origen: z.enum(['dispositivo', 'importado']).default('importado'),
    claveIdempotencia: z.string().trim().min(8).max(80),
    mediciones: z.array(esquemaMedicionImportada).min(1).max(40),
})
    .strict()
    .superRefine((informe, ctx) => {
    informe.mediciones.forEach((medicion, indice) => {
        const lateralidad = ladoDeEtiquetaDeOjo(medicion.ojo);
        if (!lateralidad) {
            ctx.addIssue({
                code: 'custom',
                message: `Ojo desconocido: «${medicion.ojo}». Use OD, OI, OS, AO u OU`,
                path: ['mediciones', indice, 'ojo'],
            });
            return;
        }
        if (!CODIGOS_OFTALMOLOGICOS.includes(medicion.codigo)) {
            ctx.addIssue({
                code: 'custom',
                message: 'No es una medición oftalmológica',
                path: ['mediciones', indice, 'codigo'],
            });
            return;
        }
        for (const problema of problemasDeObservacion({ ...medicion, lateralidad })) {
            ctx.addIssue({
                code: 'custom',
                message: problema.mensaje,
                path: ['mediciones', indice, problema.campo],
            });
        }
    });
});
//# sourceMappingURL=oftalmologia.js.map